import { useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link } from 'react-router'
import useOnclickOutside from 'react-cool-onclickoutside'
import colors from 'components/utils/colors'

export const DROPDOWN_POSITION_CENTER = 'DROPDOWN_POSITION_CENTER'
export const DROPDOWN_POSITION_RIGHT = 'DROPDOWN_POSITION_RIGHT'

const DownCaret = styled.span`
  margin: 3px 0 2px 5px;
`

const StyledDropdownActivator = styled.div`
  display: flex;
  align-items: center;
  height: ${props => props.small ? '26' : '44'}px;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  &:hover {
    cursor: pointer;
    color: ${colors.primary35};
  }
`

const LeftStyledDropdownActivator = styled.div`
  color: ${colors.secondary20};
  padding: 10px 13px;
  text-decoration: none;
  display: block;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  transition: all .25s linear;
  white-space: nowrap;
  &:hover {
    color: ${colors.secondary20};
    cursor: pointer;
    background-color: ${colors.primary90};
  }
`

const StyledRight = css`
  left: 100%;
  margin-top: -35px;
`

const DropdownContent = styled.div`
  display: ${props => props.isVisible ? 'block' : 'none'};
  position: absolute;
  min-width: 124px;
  z-index: 1;
  transition: all .55s linear;
  background-color: #FFFFFF;
  box-shadow: 0 0 3px rgba(0,0,0,.15);
  right: ${props => props.right ? '0' : 'auto'};
  transition: all .25s linear;
  ${props => props.showRight && StyledRight}

  a {
    color: ${colors.secondary20};
    padding: 10px 13px;
    text-decoration: none;
    display: block;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    transition: all .25s linear;
    white-space: nowrap;
    &:hover {
      color: ${colors.secondary20};
      cursor: pointer;
      background-color: ${colors.primary90};
    }
  }
  ${props => props.center && css`
    top: 50%;
    left: 50%;
  `}
`

const StyledA = styled.a`
  ${props => props.active && css`
    background-color: ${colors.primary80} !important;
  `}
`

const StyledDropdown = styled.div`
  position: relative;
`

const DropdownActivator = ({ name, showRight, small }) => {
  if (showRight) {
    return (
      <LeftStyledDropdownActivator className='dropdown-activator'>
        {name}
        <DownCaret className='icon-arrow-right-small-8' />
      </LeftStyledDropdownActivator>
    )
  } else {
    return (
      <StyledDropdownActivator className='dropdown-activator' small={small}>
        {name}
        <DownCaret className='icon-arrow-down-small-8' />
      </StyledDropdownActivator>
    )
  }
}

DropdownActivator.propTypes = {
  name: PropTypes.string,
  showRight: PropTypes.bool,
  small: PropTypes.bool,
}

const Dropdown = ({
  name, itemsList, onChange, selectedItemId, position, activatorComponent, activateOnClick = false,
  showRight, small, children, className,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useOnclickOutside(() => {
    setIsVisible(false)
  })

  const toggleVisible = () => {
    setIsVisible(!isVisible)
  }

  const onMouseEnter = () => {
    if (!activateOnClick) {
      setIsVisible(true)
    }
  }

  const onMouseLeave = () => {
    if (!activateOnClick) {
      setIsVisible(false)
    }
  }

  const activatorComponent_ = activatorComponent || <DropdownActivator name={name} showRight={showRight} small={small} />
  const right = position === DROPDOWN_POSITION_RIGHT
  const center = position === DROPDOWN_POSITION_CENTER

  return (
    <StyledDropdown
      className={className}
      onClick={toggleVisible}
      activateOnClick={activateOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={ref}
    >
      {activatorComponent_}
      <DropdownContent
        isVisible={isVisible}
        right={right}
        center={center}
        showRight={showRight}
      >
        {children}
        {
          !children && itemsList && itemsList.map(item => {
            const isSelectedItem = (item.id && item.id === selectedItemId)
            return (item.link
              ? (
                <Link
                  to={item.link}
                  key={item.name}
                >
                  {item.name}
                </Link>
                )
              : (
                <StyledA
                  active={isSelectedItem}
                  onClick={() => { if (onChange) onChange(item.id) }}
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </StyledA>
                )
            )
          })
        }
      </DropdownContent>
    </StyledDropdown>
  )
}

Dropdown.propTypes = {
  name: PropTypes.string,
  itemsList: PropTypes.array,
  onChange: PropTypes.func,
  selectedItemId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  position: PropTypes.string,
  activatorComponent: PropTypes.node,
  children: PropTypes.node,
  activateOnClick: PropTypes.bool,
  showRight: PropTypes.bool,
  small: PropTypes.bool,
}

export default Dropdown
